/**
 * SOVRANOVA — speech
 *
 * The companion's voice, in one place.
 *
 * WHY THIS EXISTS: the chunked long-reply playback was written for AI Session
 * and lived inside SessionView.jsx. Couples needed it, and Reflections needs it
 * too. Copying it a third time would mean three subtly different answers to
 * "does a long summary get spoken in full?" — and the one that silently
 * truncates would be the one nobody notices. One implementation, one answer.
 *
 * The pairing rule: voiceTurn.js is how SOVRANOVA LISTENS (and immediately
 * forgets the audio). This file is how it SPEAKS. Together they are the whole
 * voice surface, and both go through audioBus so only one thing is ever
 * audible at a time.
 */
import { api } from './api'
import { getGuidanceVoiceId } from './voices'

// Long replies are split on sentence boundaries rather than truncated. The cap
// is a synthesis-latency budget, not a limit on what the person may hear: the
// first chunk plays while the next is still being fetched, so chunks nobody
// reaches are never synthesised and never billed. Mirrors the server-side
// chunker in backend/routes/session.js — keep the two in step.
export const SPEAK_CHUNK_CHARS = 900
export const SPEAK_MAX_CHUNKS  = 6

// Internal markers the model uses for evidence grading — never spoken.
const MARKERS = /\[(?:PROVEN|EXPLORATORY|SPECULATIVE)\]/g

export function chunkTextForSpeech(raw) {
  const text = String(raw || '').replace(MARKERS, '').trim()
  if (!text) return []
  const sentences = text.match(/[^.!?\n]+(?:[.!?]+|\n+|$)/g) || [text]
  const chunks = []
  let buf = ''
  for (const sentence of sentences) {
    if ((buf + sentence).length > SPEAK_CHUNK_CHARS && buf) { chunks.push(buf.trim()); buf = '' }
    if (sentence.length > SPEAK_CHUNK_CHARS) {
      // A single sentence longer than a chunk gets split rather than dropped.
      for (let i = 0; i < sentence.length; i += SPEAK_CHUNK_CHARS) {
        chunks.push(sentence.slice(i, i + SPEAK_CHUNK_CHARS).trim())
      }
      continue
    }
    buf += sentence
  }
  if (buf.trim()) chunks.push(buf.trim())
  return chunks.filter(Boolean).slice(0, SPEAK_MAX_CHUNKS)
}

// Decode a base64 payload from the API into a playable audio element.
// `new Audio` is patched by audioBus, so this is registered automatically.
export function audioFromBase64(b64, mimeType) {
  const binary = atob(b64)
  const bytes  = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const blob = new Blob([bytes], { type: mimeType || 'audio/mpeg' })
  const audio = new Audio()
  audio.preload = 'auto'
  audio.src = URL.createObjectURL(blob)
  return audio
}

// Fetch one chunk. Returns null on any failure — a missing chunk must never
// break the chunks already playing.
export async function fetchChunkAudio(text) {
  try {
    const res = await api.post('/voice/speak', { text, voiceId: getGuidanceVoiceId() })
    if (!res?.audio) return null
    return audioFromBase64(res.audio, res.mimeType)
  } catch { return null }
}

/**
 * Play a prepared first chunk, then the rest, fetching each while the previous
 * is still playing so there is no gap between them.
 */
export async function playChunkSequence(firstAudio, restTexts, audioRef, isCancelled) {
  let current     = firstAudio
  let nextPromise = restTexts.length ? fetchChunkAudio(restTexts[0]) : null

  for (let i = 0; current; i++) {
    if (isCancelled()) { current.pause(); return }
    audioRef.current?.pause()
    audioRef.current = current

    const playing = current
    await new Promise(resolve => {
      let done = false
      const finish = () => { if (done) return; done = true; resolve() }
      playing.addEventListener('ended', finish, { once: true })
      playing.addEventListener('error', finish, { once: true })
      const go = () => { if (!done) playing.play().catch(finish) }
      // Play the instant enough is buffered — minimises the gap.
      if (playing.readyState >= 3) go()
      else playing.addEventListener('canplaythrough', go, { once: true })
      // Safety: if canplaythrough never fires, try anyway after a short beat.
      setTimeout(() => { if (!done && playing.paused) go() }, 350)
    })

    if (isCancelled()) return
    current     = nextPromise ? await nextPromise : null
    nextPromise = restTexts[i + 1] ? fetchChunkAudio(restTexts[i + 1]) : null
  }
}

/**
 * Speak an arbitrary block of text IN FULL. Use this for anything the person
 * is meant to receive whole — a session summary, a reflection, a close.
 */
export async function speakLong(text, audioRef, isCancelled = () => false) {
  const chunks = chunkTextForSpeech(text)
  if (!chunks.length) return
  const first = await fetchChunkAudio(chunks[0])
  if (!first || isCancelled()) return
  await playChunkSequence(first, chunks.slice(1), audioRef, isCancelled)
}

/**
 * Speak a SHORT utterance — a greeting, an acknowledgement. Deliberately capped
 * so a passing line can't turn into a monologue with synthesis latency to match.
 * If the text is something the listener needs in full, use speakLong instead.
 */
export async function speakText(text, audioRef) {
  try {
    const excerpt = String(text || '').replace(MARKERS, '').trim().slice(0, 600)
    if (!excerpt) return
    const res = await api.post('/voice/speak', { text: excerpt, voiceId: getGuidanceVoiceId() })
    if (!res?.audio) return
    const audio = audioFromBase64(res.audio, res.mimeType)
    audioRef.current?.pause()   // stop any previous speech
    audioRef.current = audio
    await audio.play()
  } catch { /* voice is never fatal */ }
}
